import moment from 'moment'
import { useContext } from 'react'
import { AppContext, AppContextType, UpgradeToMuseProDialogData } from '../AppContext'
import ElectronIPC from '../services/ElectronIPC'
import * as Constants from '../services/Constants'
import { Trans, useTranslation } from 'react-i18next'
import { UserContext, UserContextType, useUserContext } from '../UserContext'
import useRoomActions from '../services/useRoomActions'
import React from 'react'
import { RoomContext, RoomContextType } from '../RoomContext'
import useRoomContextRef from '../services/useRoomContextRef'
import useAnalytics from '../services/useAnalytics'
import { AnalyticsEvents } from '../services/AnalyticsService'
import { Button } from '@/UIComponents2/Button'
import { Alert } from '@/UIComponents2/Alert'
import CrossPlatformDialog from './components/CrossPlatformDialog'
import { DialogBody, DialogTitle } from '@/components/Dialog'
import Tooltip from '@/UIComponents2/Tooltip'
import { cn } from '@/utlis/cn'
import DialogActionButtonsSection from './components/DialogActionButtonsSection'
import campBackgroundSvg from '../../public/images/muse-camp-background.svg'

export default function UpgradeToMuseProDialogController() {
    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType

    const onCloseDialog = () => {
        if (appState.appState.showSessionIsBoostedDialog) {
            appDispatch({
                type: 'update_app_state',
                value: {
                    showSessionIsBoostedDialog: false,
                    showUpgradeToMuseProDialog: null,
                },
            })
        } else if (appState.appState.showAccountIsUpgradedDialog) {
            appDispatch({ type: 'update_app_state', value: { showAccountIsUpgradedDialog: false } })
        } else {
            appDispatch({ type: 'update_app_state', value: { showUpgradeToMuseProDialog: null } })
        }
    }

    if (appState.appState.showSessionIsBoostedDialog) {
        return (
            <CrossPlatformDialog frameName={`Dialog:UpgradeToMuseProDialog`} onDefaultClose={onCloseDialog}>
                <UpgradeToMuseProDialogContents data={{ type: 'sessionIsBoosted' }} onClose={onCloseDialog} />
            </CrossPlatformDialog>
        )
    }

    if (appState.appState.showAccountIsUpgradedDialog) {
        return (
            <CrossPlatformDialog frameName={`Dialog:UpgradeToMuseProDialog`} onDefaultClose={onCloseDialog}>
                <UpgradeToMuseProDialogContents data={{ type: 'upgradedToPro' }} onClose={onCloseDialog} />
            </CrossPlatformDialog>
        )
    }

    if (appState.appState.showUpgradeToMuseProDialog) {
        return (
            <CrossPlatformDialog
                frameName={`Dialog:UpgradeToMuseProDialog`}
                onDefaultClose={onCloseDialog}
                forceBrowserModal={appState.appState.showUpgradeToMuseProDialog.forceBrowserModal ?? false}
                browserZIndex={23}
            >
                <UpgradeToMuseProDialogContents
                    data={appState.appState.showUpgradeToMuseProDialog}
                    onClose={onCloseDialog}
                />
            </CrossPlatformDialog>
        )
    } else {
        return null
    }
}

function UpgradeToMuseProDialogContents({ data, onClose }: { data: UpgradeToMuseProDialogData; onClose: () => void }) {
    const { t } = useTranslation()
    const { logUserActivityWithContext } = useAnalytics()
    const [userState] = useUserContext()
    const { roomStateRef } = useRoomContextRef()

    const onLearnMoreClicked = () => {
        logUserActivityWithContext(AnalyticsEvents.CLICKED_LEARN_MORE_BOOST_BUTTON)
        ElectronIPC.openLinkInBrowser(Constants.links.about_boosts_link)
    }

    const onViewPlansClicked = (params?: { close?: boolean }) => {
        logUserActivityWithContext(AnalyticsEvents.VIEWED_UPGRADE_PAGE, { feature: data.type })
        ElectronIPC.openLinkInBrowser(Constants.links.muse_pro_plans_link)
        if (params?.close) {
            onClose()
        }
    }

    const onFeatureWaitlistClicked = () => {
        logUserActivityWithContext(AnalyticsEvents.CLICKED_FEATURE_WAITLIST_BUTTON, { feature: data.type })
        const link = Constants.links.feature_waitlist_form.replace('{email}', userState.profile?.email ?? '')
        ElectronIPC.openLinkInBrowser(link)
        onClose()
    }

    if (data.type === 'sessionDurationLimitRunningOut' || data.type === 'boost') {
        return (
            <Template
                data={data}
                title={t('The session is about to end')}
                onViewPlansClicked={onViewPlansClicked}
                onClose={onClose}
            >
                <div>
                    {t(
                        'Your free session ends after 1 hour and files will be deleted. Use a boost to add 1 more hour and unlock all Muse Pro features.'
                    )}
                </div>
                <div>
                    <Button variant="link" onClick={onLearnMoreClicked}>
                        {t('Learn more')}
                    </Button>
                </div>
            </Template>
        )
    } else if (data.type === 'premiumFeature:noDeviceCountLimit') {
        const n = roomStateRef.current.roomTierPolicy.maxAudioDeviceCount
        logUserActivityWithContext(AnalyticsEvents.UPGRADE_TO_MUSE_PRO_POPUP_TRIGGERED, {
            feature: 'noDeviceCountLimit',
        })
        return (
            <Template
                data={data}
                title={t('This is a premium feature')}
                onViewPlansClicked={onViewPlansClicked}
                onClose={onClose}
            >
                <>
                    <div className="mb-3">
                        {t(
                            'Free sessions allow 2 devices max. Use a boost to connect more devices and unlock Muse Pro features for 1 hour.'
                        )}
                    </div>
                    <PremiumFeatureCommonText onLearnMoreClicked={onLearnMoreClicked} />
                </>
            </Template>
        )
    } else if (data.type === 'premiumFeature:noCollaboratorCountLimit') {
        logUserActivityWithContext(AnalyticsEvents.UPGRADE_TO_MUSE_PRO_POPUP_TRIGGERED, {
            feature: 'noCollaboratorCountLimit',
        })
        return (
            <Template
                data={data}
                title={t('This is a premium feature')}
                onViewPlansClicked={onViewPlansClicked}
                onClose={onClose}
            >
                <>
                    <div className="mb-3">
                        {t(
                            'Free sessions allow up to 3 participants. Use a boost to invite more collaborators and unlock Muse Pro features for 1 hour.'
                        )}
                    </div>
                    <PremiumFeatureCommonText onLearnMoreClicked={onLearnMoreClicked} />
                </>
            </Template>
        )
    } else if (data.type === 'premiumFeature:useProAudio') {
        logUserActivityWithContext(AnalyticsEvents.UPGRADE_TO_MUSE_PRO_POPUP_TRIGGERED, { feature: 'useProAudio' })
        return (
            <Template
                data={data}
                title={t('This is a premium feature')}
                onViewPlansClicked={onViewPlansClicked}
                onClose={onClose}
            >
                <div className="mb-3">
                    {t(
                        'Free sessions use standard high quality audio. Use a boost to share Pro Audio and unlock Muse Pro features for 1 hour.'
                    )}
                </div>
                <PremiumFeatureCommonText onLearnMoreClicked={onLearnMoreClicked} />
            </Template>
        )
    } else if (data.type === 'premiumFeature:noChannelCountLimit') {
        logUserActivityWithContext(AnalyticsEvents.UPGRADE_TO_MUSE_PRO_POPUP_TRIGGERED, {
            feature: 'noChannelCountLimit',
        })
        return (
            <Template
                data={data}
                title={t('This is a premium feature')}
                onViewPlansClicked={onViewPlansClicked}
                onClose={onClose}
            >
                <div className="mb-3">{t('You can boost your session to select more channels.')}</div>
                <PremiumFeatureCommonText onLearnMoreClicked={onLearnMoreClicked} />
            </Template>
        )
    } else if (data.type === 'sessionIsBoosted') {
        return <SessionIsBoostedContent onViewPlansClicked={onViewPlansClicked} onClose={onClose} />
    } else if (data.type === 'upgradedToPro') {
        return <AccountIsUpgradedContent onClose={onClose} />
    } else if (data.type === 'sessionIsFull:host') {
        return (
            <Template
                data={data}
                title="This session is full"
                onViewPlansClicked={onViewPlansClicked}
                onClose={onClose}
            >
                <div>
                    <span className="text-muse-yellow">{data.username || ''}</span> wants to join this session.
                </div>
                <div>You can use a boost to let them join.</div>
                <div>
                    <Button variant="link" onClick={onLearnMoreClicked}>
                        Learn more about boosts.
                    </Button>
                </div>
                <div>
                    You can also{' '}
                    <Button variant="link" onClick={() => onViewPlansClicked()}>
                        upgrade your plan
                    </Button>{' '}
                    to remove session limits.
                </div>
            </Template>
        )
    } else if (data.type === 'sessionIsFull:caller') {
        return (
            <Template
                data={data}
                title="This session is full"
                onViewPlansClicked={onViewPlansClicked}
                onClose={onClose}
            >
                <div className="mb-3">You can use a boost to join or wait for the host to let you in.</div>
                <Button className="mb-3" variant="link" onClick={onLearnMoreClicked}>
                    Learn more about boosts.
                </Button>
                <div className="mb-3">
                    You can also{' '}
                    <Button variant="link" onClick={() => onViewPlansClicked()}>
                        upgrade your plan
                    </Button>{' '}
                    to remove session limits.
                </div>
            </Template>
        )
    } else if (data.type === 'sessionIsFull:guest') {
        return (
            <RoomFullForGuestsContent
                onViewPlansClicked={onViewPlansClicked}
                onLearnMoreClicked={onLearnMoreClicked}
                onClose={onClose}
            />
        )
    } else if (data.type === 'premiumFeature:permanentRoom') {
        return <MusePermanentRoomContent onViewPlansClicked={onViewPlansClicked} onClose={onClose} />
    } else if (data.type === 'premiumFeature:museCamp') {
        return <MuseCampContent onFeatureWaitlistClicked={onFeatureWaitlistClicked} onClose={onClose} />
    } else if (data.type === 'premiumFeature:livestream') {
        return <MuseLivestreamContent onFeatureWaitlistClicked={onFeatureWaitlistClicked} onClose={onClose} />
    } else if (data.type === 'premiumFeature:realtimeSession') {
        return <MuseRealtimeSessionContent onFeatureWaitlistClicked={onFeatureWaitlistClicked} onClose={onClose} />
    } else {
        return <LegacyDefaultContent onViewPlansClicked={onViewPlansClicked} onClose={onClose} />
    }
}

const PremiumFeatureCommonText = ({ onLearnMoreClicked }: { onLearnMoreClicked: () => void }) => {
    const { t } = useTranslation()
    return (
        <>
            <Button className="mb-3" variant="link" onClick={onLearnMoreClicked}>
                {t('Learn more')}
            </Button>
        </>
    )
}

const Template: React.FunctionComponent<{
    title: string
    data: UpgradeToMuseProDialogData
    onViewPlansClicked: (params?: { close?: boolean }) => void
    onClose: () => void
    children?: React.ReactNode
}> = ({ title, data, onViewPlansClicked, onClose, children }) => {
    const { t } = useTranslation()

    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { state } = useContext(UserContext) as UserContextType
    const { state: roomState } = useContext(RoomContext) as RoomContextType

    const { boostRoom } = useRoomActions()
    const { logUserActivityWithContext } = useAnalytics()
    const onBoostClick = async () => {
        // sets `roomState.isBoostBeingApplied` appropriately.
        await boostRoom(data.type)
        logUserActivityWithContext(AnalyticsEvents.CLICKED_BOOST_SESSION_BUTTON, {
            boostsMax: state.profile?.boostsMax ?? 2,
            boostsRemaining: state.profile?.boostsRemaining ?? 0,
            boostType: data.type,
        })
        onClose()
    }

    const openContactLink = () => {
        ElectronIPC.openLinkInBrowser(Constants.links.website_bugreports)
    }

    const setDontShowAgainWhySessionLimit = () => {
        appDispatch({
            type: 'update_app_settings',
            value: { dontShowAgainWhySessionLimit: true },
        })
    }

    const boostsMax = state.profile?.boostsMax ?? 2
    const boostsRemaining = state.profile?.boostsRemaining ?? 0
    const isBoostAvailable = boostsRemaining > 0
    const isInSession = !!roomState.roomUrn
    const boostButtonDisabled = roomState.isBoostBeingApplied || !isInSession
    const boostTooltip = isInSession ? null : t('You must join a session before you can use a boost')

    const showWhyDurationLimit =
        !state.profile?.policy?.roomTierPolicy?.sessionDurationLimitMins &&
        !appState.appSettings.dontShowAgainWhySessionLimit

    return (
        <>
            <DialogBody>
                <DialogTitle>{title}</DialogTitle>

                {children}

                <div className="flex flex-col px-3 py-3 my-3 items-center rounded-[20px] bg-white/5">
                    <div className="mb-1">
                        {'Boosts remaining: '}
                        {boostsRemaining} of {boostsMax}
                    </div>

                    <div className="text-xs text-white/80">
                        {state.profile?.boostNextRechargeDate ? (
                            <>
                                {'Reloads '}
                                {moment(state.profile?.boostNextRechargeDate).format('MM/DD')}
                            </>
                        ) : (
                            'Reloads monthly'
                        )}
                    </div>
                </div>
                {showWhyDurationLimit && (
                    <Alert variant="solid" level="warning">
                        Why am I seeing this?
                        <br />
                        This session was hosted with a free plan. If you think this was a mistake,{' '}
                        <Button variant="link" onClick={openContactLink}>
                            let us know
                        </Button>
                        .<br />
                        <Button variant="link" onClick={setDontShowAgainWhySessionLimit}>
                            Don't show again
                        </Button>
                    </Alert>
                )}
                {!state.profile?.emailVerified && (
                    <Alert variant={'solid'} level="warning">
                        Your account isn't verified. Verify your email to get boosts.
                    </Alert>
                )}
            </DialogBody>

            <BottomButtonContainer>
                {isBoostAvailable ? (
                    <>
                        <Button
                            variant="primary"
                            className="flex-1"
                            onClick={() => onViewPlansClicked({ close: true })}
                        >
                            {t('View Plans') || 'View Plans'}
                        </Button>
                        <Tooltip text={boostTooltip}>
                            <Button
                                variant="primary"
                                disabled={boostButtonDisabled}
                                className={cn('flex-1', boostButtonDisabled && 'opacity-60 cursor-not-allowed')}
                                onClick={onBoostClick}
                            >
                                {t('Use Free Boost')}
                            </Button>
                        </Tooltip>
                    </>
                ) : (
                    <>
                        <Button variant="secondary" className="flex-1" onClick={onClose}>
                            {t('Close') || 'Close'}
                        </Button>
                        <Button
                            variant="primary"
                            className="flex-1"
                            onClick={() => onViewPlansClicked({ close: true })}
                        >
                            {t('View Plans') || 'View Plans'}
                        </Button>
                    </>
                )}
            </BottomButtonContainer>
        </>
    )
}

function SessionIsBoostedContent({
    onViewPlansClicked,
    onClose,
}: {
    onViewPlansClicked: (params?: { close?: boolean }) => void
    onClose: () => void
}) {
    const { t } = useTranslation()

    const { dispatch: appDispatch } = useContext(AppContext) as AppContextType

    const onCloseWithDontShowAgain = () => {
        appDispatch({
            type: 'update_app_settings',
            value: { doNotShowRoomBoostedDialog: true },
        })

        onClose()
    }

    return (
        <>
            <DialogBody>
                <DialogTitle>This session is boosted</DialogTitle>

                <div>{t('For the next hour, this session has Muse Pro features:')}</div>
                <div className="flex flex-col ml-4 mb-4 mt-1">
                    <div>- {t('1 extra hour of session time')}</div>
                    <div>- {t('Up to 40 collaborators')}</div>
                    <div>- {t('Unlimited devices')}</div>
                    <div>- {t('Sample rate & latency control')}</div>
                    <div>- {t('Pro Audio (lossless PCM 32-bit)')}</div>
                    <div>- {t('Multi-channel support')}</div>
                </div>

                <div className="mb-3">
                    <Trans
                        i18nKey="<0>Upgrade your plan</0> anytime for unlimited session time, permanent sessions, and full Pro access."
                        components={[<Button variant="link" onClick={() => onViewPlansClicked()} />]}
                    />
                </div>
            </DialogBody>

            <BottomButtonContainer>
                <Button variant="secondary" className="flex-1" onClick={onCloseWithDontShowAgain}>
                    {t("Don't show again") || "Don't show again"}
                </Button>
                <Button variant="primary" className="flex-1" onClick={onClose}>
                    {t('OK') || 'OK'}
                </Button>
            </BottomButtonContainer>
        </>
    )
}

function AccountIsUpgradedContent({ onClose }: { onClose: () => void }) {
    const { t } = useTranslation()

    return (
        <>
            <DialogBody>
                <DialogTitle>Welcome to Muse Pro!</DialogTitle>

                <div>{t('You now have full access to:')}</div>
                <div className="flex flex-col ml-4 mb-4 mt-1">
                    <div>- {t('Unlimited session time')}</div>
                    <div>- {t('Permanent sessions')}</div>
                    <div>- {t('Up to 40 collaborators')}</div>
                    <div>- {t('Unlimited devices')}</div>
                    <div>- {t('Sample rate and latency control')}</div>
                    <div>- {t('Pro Audio (lossless PCM 32-bit)')}</div>
                    <div>- {t('Multi-channel support')}</div>
                </div>
            </DialogBody>

            <BottomButtonContainer>
                <Button variant="primary" className="flex-1" onClick={onClose}>
                    {t('OK') || 'OK'}
                </Button>
            </BottomButtonContainer>
        </>
    )
}
function RoomFullForGuestsContent({
    onViewPlansClicked,
    onLearnMoreClicked,
    onClose,
}: {
    onViewPlansClicked: (params?: { close?: boolean }) => void
    onLearnMoreClicked: () => void
    onClose: () => void
}) {
    const { t } = useTranslation()

    const onCreateAccount = () => {
        ElectronIPC.openLinkInBrowser(Constants.links.website_login)
        onClose()
    }

    return (
        <>
            <DialogBody>
                <DialogTitle>This session is full</DialogTitle>

                <div>
                    Waiting for the host to let you in. Alternatively, you can create an account and use a boost to
                    join.
                </div>
                <Button className="mb-3" variant="link" onClick={onLearnMoreClicked}>
                    Learn more about boosts.
                </Button>

                <div className="mb-3">
                    <Button variant="link" onClick={() => onViewPlansClicked()}>
                        Upgrade your plan
                    </Button>{' '}
                    for unlimited session time, permanent sessions, and additional features including the ones listed
                    above.
                </div>
            </DialogBody>

            <BottomButtonContainer>
                <Button variant="secondary" className="flex-1" onClick={onClose}>
                    {t('Cancel') || 'Cancel'}
                </Button>
                <Button variant="primary" className="flex-1" onClick={onCreateAccount}>
                    {t('Create account') || 'Create account'}
                </Button>
            </BottomButtonContainer>
        </>
    )
}

type MuseCampContentProps = {
    onFeatureWaitlistClicked: () => void
    onClose: () => void
}
function MuseCampContent(props: MuseCampContentProps) {
    const { t } = useTranslation()

    return (
        <>
            <div className="min-h-[189px]">
                <img src={campBackgroundSvg} className="-mt-10" />
            </div>
            <DialogTitle className="pl-8">{t('Muse Camp')}</DialogTitle>
            <DialogBody className="pl-8 pb-6">
                {t(
                    'Camp sessions allow the organizer to manage studio rooms so participants can split up to collaborate.'
                )}
            </DialogBody>
            <DialogActionButtonsSection
                leftButtonText={t('Close')}
                onLeftButtonClicked={() => props.onClose()}
                rightButtonText={t('Join Waitlist')}
                onRightButtonClicked={() => props.onFeatureWaitlistClicked()}
            />
        </>
    )
}

type MuseRealtimeSessionContentProps = {
    onFeatureWaitlistClicked: () => void
    onClose: () => void
}
function MuseRealtimeSessionContent(props: MuseRealtimeSessionContentProps) {
    const { t } = useTranslation()

    return (
        <>
            <div className="min-h-[189px]">
                <img src={campBackgroundSvg} className="-mt-10" />
            </div>
            <DialogTitle className="pl-8">{t('Muse Realtime Sessions')}</DialogTitle>
            <DialogBody className="pl-8 pb-6">
                {t(
                    'Have a two person jamming session optimized for realtime audio. Geographic distance limitations apply.'
                )}
            </DialogBody>
            <DialogActionButtonsSection
                leftButtonText={t('Close')}
                onLeftButtonClicked={() => props.onClose()}
                rightButtonText={t('Join Waitlist')}
                onRightButtonClicked={() => props.onFeatureWaitlistClicked()}
            />
        </>
    )
}

type MuseLivestreamContentProps = {
    onFeatureWaitlistClicked: () => void
    onClose: () => void
}
function MuseLivestreamContent(props: MuseLivestreamContentProps) {
    const { t } = useTranslation()

    return (
        <>
            <div className="min-h-[189px]">
                <img src={campBackgroundSvg} className="-mt-10" />
            </div>
            <DialogTitle className="pl-8">{t('Muse Live Streaming')}</DialogTitle>
            <DialogBody className="pl-8 pb-6">
                {t('Livestream sessions allow viewers to join and participate in the chat.')}
            </DialogBody>
            <DialogActionButtonsSection
                leftButtonText={t('Close')}
                onLeftButtonClicked={() => props.onClose()}
                rightButtonText={t('Join Waitlist')}
                onRightButtonClicked={() => props.onFeatureWaitlistClicked()}
            />
        </>
    )
}

type MusePermanentRoomContentProps = {
    onViewPlansClicked: () => void
    onClose: () => void
}
function MusePermanentRoomContent(props: MusePermanentRoomContentProps) {
    const { t } = useTranslation()

    return (
        <>
            <div className="min-h-[189px]">
                <img src={campBackgroundSvg} className="-mt-10" height={189} />
            </div>
            <DialogTitle className="pl-8">{t('Muse Permanent Rooms')}</DialogTitle>
            <DialogBody className="pl-8 pb-6">
                {t('Permanent rooms are always available for collaborators to join and hang out.')}
            </DialogBody>
            <DialogActionButtonsSection
                leftButtonText={t('Close')}
                onLeftButtonClicked={() => props.onClose()}
                rightButtonText={t('Upgrade')}
                onRightButtonClicked={() => props.onViewPlansClicked()}
            />
        </>
    )
}

function LegacyDefaultContent({
    onViewPlansClicked,
    onClose,
}: {
    onViewPlansClicked: (params?: { close?: boolean }) => void
    onClose: () => void
}) {
    const { t } = useTranslation()

    return (
        <>
            <DialogBody>
                <DialogTitle>{t('Upgrade to Muse Pro')}</DialogTitle>

                <div>
                    {t(
                        'Get access to higher quality audio, multi-channel support, sample rate selection, ' +
                            'buffer control, and more when you upgrade to Muse Pro.'
                    )}
                </div>
            </DialogBody>
            <BottomButtonContainer>
                <Button variant="primary" className="flex-1" onClick={() => onViewPlansClicked({ close: true })}>
                    {t('View plans') || 'View plans'}
                </Button>
            </BottomButtonContainer>
        </>
    )
}

function BottomButtonContainer({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex flex-1 w-full pt-6">
            <div className="flex gap-2 w-full mt-auto">{children}</div>
        </div>
    )
}
